import { useNavigate, useRouterState } from "@tanstack/react-router"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { TabsList, TabsTrigger, TabsWrapper } from "design-system/molecules/Tabs"
import { TFunction } from "i18next"
import {
  Activity,
  BookOpenText,
  ChartNoAxesCombined,
  GitBranch,
  PencilRuler,
  Settings,
} from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

type Props = {
  id: string
  disabled?: boolean
  isLogistics?: boolean
}

function getTabs(t: TFunction, disabled?: boolean, isLogistics?: boolean) {
  return [
    {
      label: t("campaigns.detail.tabs.insights"),
      value: "insights",
      disabled,
      icon: ChartNoAxesCombined,
    },
    {
      label: t("campaigns.detail.tabs.configuration"),
      value: "configuration",
      disabled,
      icon: Settings,
    },
    {
      label: t("campaigns.detail.tabs.workflows"),
      value: "workflows",
      disabled,
      icon: GitBranch,
    },
    {
      label: t("campaigns.detail.tabs.capabilities"),
      value: "capabilities",
      disabled,
      icon: PencilRuler,
    },
    {
      label: t("campaigns.detail.tabs.knowledgeBase"),
      value: "knowledge-base",
      disabled,
      icon: BookOpenText,
    },
    {
      label: t("campaigns.detail.tabs.activity"),
      value: "activity",
      disabled,
      icon: Activity,
    },
  ].map((tab) => {
    const isBlockedByLogistics =
      isLogistics &&
      new Set(["configuration", "workflows", "capabilities", "knowledge-base"]).has(tab.value)

    return {
      ...tab,
      disabled: disabled || isBlockedByLogistics,
      disabledTooltip: isBlockedByLogistics
        ? t("campaigns.detail.logisticsManagedTooltip")
        : undefined,
    }
  })
}

export const CampaignTabs = ({ id, disabled, isLogistics }: Props) => {
  const { t } = useTranslation()
  const { location } = useRouterState()
  const navigate = useNavigate()

  const currentTab = useMemo(() => {
    if (location.pathname.includes("/configuration")) return "configuration"
    if (location.pathname.includes("/workflows")) return "workflows"
    if (location.pathname.includes("/capabilities")) return "capabilities"
    if (location.pathname.includes("/knowledge-base")) return "knowledge-base"
    if (location.pathname.includes("/insights")) return "insights"
    if (location.pathname.includes("/activity")) return "activity"
    return "insights"
  }, [location.pathname])

  function onTabChange(tab: string) {
    navigate({ to: `/campaigns/${id}/${tab}` })
  }

  const isPlayground = location.pathname.includes("/playground")

  const tabs = useMemo(() => getTabs(t, disabled, isLogistics), [t, disabled, isLogistics])

  return (
    <div className="w-full flex flex-col lg:flex-row gap-2 justify-between">
      {!isPlayground && (
        <TabsWrapper
          value={currentTab}
          onValueChange={onTabChange}
          className="max-w-full overflow-x-auto"
        >
          <TabsList>
            {tabs.map((tab) => {
              const content = (
                <TabsTrigger value={tab.value} disabled={tab.disabled} key={tab.value}>
                  <tab.icon className="size-4" />
                  <span className="text-sm font-medium">{tab.label}</span>
                </TabsTrigger>
              )

              if (tab.disabledTooltip) {
                return (
                  <Tooltip key={tab.value}>
                    <TooltipTrigger asChild>{content}</TooltipTrigger>
                    <TooltipContent>{tab.disabledTooltip}</TooltipContent>
                  </Tooltip>
                )
              }

              return content
            })}
          </TabsList>
        </TabsWrapper>
      )}
    </div>
  )
}
